import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getContrastTextColor } from '../lib/utils';
import { PAGE_VISIBILITY_ITEMS } from '../lib/pageVisibility';
import PermissionsSnapshotDialog from '../components/PermissionsSnapshotDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Phone,
  Mail,
  Users,
  Edit,
  Trash2,
  Loader2,
  UserPlus,
  Bell,
  BellOff,
  Key,
  Send,
  LogOut,
  UserCheck,
  Shield,
  CheckCircle,
  Briefcase,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import StatTile from '../components/StatTile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALENDAR_COLORS = [
  // Row 1 - Warm colors
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#facc15',
  // Row 2 - Greens
  '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4',
  // Row 3 - Blues
  '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  // Row 4 - Pinks/Purples
  '#d946ef', '#ec4899', '#f43f5e', '#be185d', '#9333ea',
  // Row 5 - Neutrals/Earth tones
  '#78716c', '#57534e', '#0d9488', '#059669', '#047857'
];

export default function Team() {
  const { isAdmin, user, impersonate } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showSnapshotDialog, setShowSnapshotDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  
  // Push notification status for team
  const [pushStatus, setPushStatus] = useState({});

  const [editForm, setEditForm] = useState({
    full_name: '',
    title: '',
    phone: '',
    email: '',
    calendar_color: '#f59e0b',
    active: true,
    role: 'technician',
    permissions: {
      // Jobs
      create_jobs: false,
      edit_jobs: false,
      delete_jobs: false,
      assign_jobs: false,
      assign_equipment_to_jobs: false,
      // Proposals
      view_proposals: false,
      create_proposals: false,
      // Certifications
      manage_certifications: false,
      // Visibility
      view_reports: false,
      view_financials: false,
      office_access: false,
      view_all_jobs: false,
      // Management
      manage_team: false,
      delete_team_members: false,
      manage_contacts: false,
      manage_services: false,
      manage_equipment: false,
      manage_calendar_events: false,
      // Approvals
      can_approve_field_reports: false,
      can_receive_approval_notifications: false,
      can_approve_maintenance_forms: false,
      can_receive_maintenance_notifications: true,
      can_receive_expense_notifications: false,
      // Expenses
      expense_any_job: false,
    }
  });

  const [inviteForm, setInviteForm] = useState({
    email: '',
    full_name: ''
  });

  const [createUserForm, setCreateUserForm] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'technician',
    title: '',
    phone: ''
  });

  useEffect(() => {
    // fetchTeam runs in its own effect below (triggered by showInactive toggle)
    if (isAdmin()) {
      fetchInvites();
      fetchPushStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showInactive, setShowInactive] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreForm, setRestoreForm] = useState({
    full_name: '', email: '', phone: '', role: 'technician', title: 'Field Tech',
  });

  const fetchTeam = async () => {
    try {
      const url = showInactive ? `${API}/team?include_inactive=true` : `${API}/team`;
      const response = await axios.get(url);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever the admin toggles "show inactive" so historical
  // members (e.g. Nate Upchurch after deactivation) appear without a
  // full page refresh.
  useEffect(() => {
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const handleReactivate = async (memberId) => {
    try {
      await axios.post(`${API}/team/${memberId}/reactivate`);
      toast.success('Team member reactivated');
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate');
    }
  };

  const handleRestoreDeleted = async () => {
    if (!restoreForm.full_name || !restoreForm.email) {
      toast.error('Full name and email are required');
      return;
    }
    try {
      const r = await axios.post(`${API}/team/restore-deleted`, restoreForm);
      toast.success(r.data?.message || 'Restored');
      setRestoreOpen(false);
      setRestoreForm({ full_name: '', email: '', phone: '', role: 'technician', title: 'Field Tech' });
      setShowInactive(true); // surface the restored (inactive) record
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const fetchInvites = async () => {
    try {
      const response = await axios.get(`${API}/auth/invites`);
      setInvites(response.data);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  };

  const fetchPushStatus = async () => {
    if (!isAdmin()) return;
    try {
      const response = await axios.get(`${API}/push/team-status`);
      // Convert to a map for easy lookup
      const statusMap = {};
      response.data.team.forEach(member => {
        statusMap[member.user_id] = {
          enabled: member.push_enabled,
          deviceCount: member.device_count
        };
      });
      setPushStatus(statusMap);
    } catch (error) {
      console.error('Failed to fetch push status:', error);
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setEditForm({
      full_name: member.full_name,
      title: member.title || '',
      phone: member.phone || '',
      email: member.email,
      calendar_color: member.calendar_color || '#f59e0b',
      active: member.active,
      role: member.role || 'technician',
      permissions: member.permissions || {
        create_jobs: false,
        edit_jobs: false,
        delete_jobs: false,
        view_reports: false,
        manage_team: false
      },
      page_visibility: member.page_visibility || {},
      // Per-channel notification subscriptions — default each missing
      // channel to true so existing users keep receiving every alert
      // until an admin explicitly opts them out from this dialog.
      notification_subscriptions: {
        job_status: member.notification_subscriptions?.job_status !== false,
        fr_pending: member.notification_subscriptions?.fr_pending !== false,
        fr_approved: member.notification_subscriptions?.fr_approved !== false,
        fr_ready_to_package: member.notification_subscriptions?.fr_ready_to_package !== false,
      },
    });
    setShowEditDialog(true);
  };

  // Test as User: admin uses this to impersonate a team member and walk through the app as them
  const handleImpersonate = async (member) => {
    if (!window.confirm(`Test the app as ${member.full_name}?\n\nYou'll see exactly what they see — every page, every button.\n\nA persistent banner stays at the top so you can return to your admin session at any time.`)) {
      return;
    }
    try {
      await impersonate(member.id);
      // window.location reload happens inside impersonate()
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start impersonation');
    }
  };

  const handleSaveMember = async () => {
    setSaving(true);
    try {
      // Mirror approve maintenance/equipment forms — backend supports both keys for compatibility
      const perms = { ...(editForm.permissions || {}) };
      if (perms.can_approve_maintenance_forms || perms.can_approve_equipment_forms) {
        perms.can_approve_maintenance_forms = true;
        perms.can_approve_equipment_forms = true;
      } else {
        perms.can_approve_maintenance_forms = false;
        perms.can_approve_equipment_forms = false;
      }
      const payload = { ...editForm, permissions: perms };
      await axios.put(`${API}/team/${selectedMember.id}`, payload);
      toast.success('Team member updated');
      setShowEditDialog(false);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/team/${selectedMember.id}`);
      toast.success('Team member removed');
      setShowDeleteDialog(false);
      setSelectedMember(null);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    } finally {
      setSaving(false);
    }
  };

  // Password Reset Functions
  const handleSendResetLink = async () => {
    if (!selectedMember) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/auth/admin/send-reset-link`, {
        user_id: selectedMember.id
      });
      if (response.data.email_sent) {
        toast.success(`Password reset link sent to ${selectedMember.email}`);
        setShowPasswordResetDialog(false);
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setSaving(false);
    }
  };

  const handleSetTempPassword = async () => {
    if (!selectedMember) return;
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/auth/admin/reset-password`,
        { user_id: selectedMember.id, send_email_notification: true }
      );
      setResetResult({
        password: response.data.temporary_password,
        email_sent: response.data.email_sent,
        user_name: response.data.user_name
      });
      toast.success('Temporary password created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };


  const handleForceLogout = async (member) => {
    try {
      await axios.post(`${API}/auth/revoke-sessions/${member.id}`);
      toast.success(`All sessions revoked for ${member.full_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke sessions');
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email || !inviteForm.full_name) {
      toast.error('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/auth/invite`, {
        email: inviteForm.email,
        full_name: inviteForm.full_name,
        role: 'technician'
      });
      
      if (response.data.email_sent) {
        toast.success(`Invite email sent to ${inviteForm.email}! Code: ${response.data.invite_code}`);
      } else {
        toast.success(`Invite created! Code: ${response.data.invite_code} (email not sent - share manually)`);
      }
      
      setShowInviteDialog(false);
      setInviteForm({ email: '', full_name: '' });
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createUserForm.email || !createUserForm.full_name || !createUserForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (createUserForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/auth/create-user`, null, {
        params: {
          email: createUserForm.email,
          full_name: createUserForm.full_name,
          password: createUserForm.password,
          role: createUserForm.role,
          title: createUserForm.title || null,
          phone: createUserForm.phone || null
        }
      });
      
      // Store credentials for display
      setCreatedCredentials(response.data.credentials);
      toast.success('User account created successfully!');
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseCreateUser = () => {
    setShowCreateUserDialog(false);
    setCreatedCredentials(null);
    setCreateUserForm({
      email: '',
      full_name: '',
      password: '',
      role: 'technician',
      title: '',
      phone: ''
    });
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCreateUserForm(prev => ({ ...prev, password }));
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/auth/invites/${inviteId}`);
      toast.success('Invite deleted');
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invite');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-slate-200 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-slate-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
          Team
        </h1>
        {isAdmin() && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => setShowSnapshotDialog(true)}
              data-testid="permissions-snapshot-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              Permissions Snapshot
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => setShowInviteDialog(true)}
              data-testid="invite-team-member-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
            <Button
              size="sm"
              className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={() => setShowCreateUserDialog(true)}
              data-testid="create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User Account
            </Button>
            <label
              className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-700 cursor-pointer hover:bg-slate-50"
              data-testid="show-inactive-toggle"
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-amber-500"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive
            </label>
            <Button
              variant="outline"
              size="sm"
              className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => setRestoreOpen(true)}
              data-testid="restore-deleted-btn"
              title="Re-create a previously hard-deleted team member as inactive so jobs referencing them stay linked"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Restore Deleted
            </Button>
          </div>
        )}
      </div>

      {/* Push Notification Status Summary */}
      {isAdmin() && members.length > 0 && (
        <Card className="border-slate-200 bg-gradient-to-r from-green-50 to-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Push Notifications Status</h4>
                  <p className="text-sm text-slate-600">
                    {Object.values(pushStatus).filter(s => s.enabled).length} of {members.length} team members have push enabled
                  </p>
                </div>
              </div>
              {Object.values(pushStatus).filter(s => s.enabled).length < members.length && (
                <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg">
                  {members.length - Object.values(pushStatus).filter(s => s.enabled).length} need to enable notifications
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Invites */}
      {isAdmin() && invites.filter(i => i.status === 'pending').length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed']">Pending Invites</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invites.filter(i => i.status === 'pending').map(invite => (
                <div key={invite.id} className="flex items-center justify-between p-3 bg-white rounded-md border border-yellow-200">
                  <div>
                    <p className="font-medium text-slate-900">{invite.full_name || invite.email}</p>
                    <p className="text-sm text-slate-500">{invite.email}</p>
                    {invite.email_sent && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> Email sent
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <Badge variant="outline" className="font-mono">{invite.invite_code}</Badge>
                      <p className="text-xs text-slate-400 mt-1">Share this code</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                      onClick={() => handleDeleteInvite(invite.id)}
                      title="Delete invite"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members Grid */}
      {members.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No team members</h3>
            <p className="text-slate-500">Invite team members to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map(member => (
            <Card 
              key={member.id} 
              className={`border-slate-200 hover:border-amber-500/50 transition-all ${!member.active ? 'opacity-60' : ''}`}
              data-testid={`team-member-card-${member.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: member.calendar_color || '#f59e0b', color: getContrastTextColor(member.calendar_color || '#f59e0b') }}
                  >
                    {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 truncate">{member.full_name}</h3>
                      {!member.active && (
                        <Badge variant="outline" className="text-xs">Inactive</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{member.title || member.role}</p>
                    
                    <div className="mt-3 space-y-1">
                      {member.phone && (
                        <a href={`tel:${member.phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-amber-600">
                          <Phone className="w-4 h-4" />
                          {member.phone}
                        </a>
                      )}
                      <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-amber-600">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{member.email}</span>
                      </a>
                      
                      {/* Push notification status */}
                      {isAdmin() && (
                        <div className="flex items-center gap-2 text-sm mt-2">
                          {pushStatus[member.id]?.enabled ? (
                            <span className="flex items-center gap-1.5 text-green-600">
                              <Bell className="w-4 h-4" />
                              Push enabled
                              {pushStatus[member.id]?.deviceCount > 1 && (
                                <span className="text-xs text-green-500">({pushStatus[member.id].deviceCount} devices)</span>
                              )}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-slate-400">
                              <BellOff className="w-4 h-4" />
                              No push notifications
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {isAdmin() && (
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleEditMember(member)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    {member.id !== user?.id && member.active !== false && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-blue-600 hover:bg-blue-50"
                        onClick={() => handleImpersonate(member)}
                        title="Test the app as this user"
                        data-testid={`impersonate-${member.id}`}
                      >
                        <UserCheck className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-amber-600 hover:bg-amber-50"
                      onClick={() => { 
                        setSelectedMember(member); 
                        setResetResult(null);
                        setShowPasswordResetDialog(true); 
                      }}
                      title="Reset Password"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-orange-600 hover:bg-orange-50"
                      onClick={() => handleForceLogout(member)}
                      title="Force Logout (all devices)"
                      data-testid={`force-logout-${member.id}`}
                    >
                      <LogOut className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => { setSelectedMember(member); setShowDeleteDialog(true); }}
                      title="Deactivate (preserves timesheet history; reversible)"
                      data-testid={`deactivate-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {member.active === false && member.id !== user?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 hover:bg-green-50"
                        onClick={() => handleReactivate(member.id)}
                        title="Reactivate this team member"
                        data-testid={`reactivate-${member.id}`}
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        Reactivate
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-h-[90vh] overflow-visible">
          <div className="max-h-[80vh] overflow-y-auto pr-2">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">Edit Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                className="bg-slate-50"
                data-testid="edit-member-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Field Technician, Project Manager"
                className="bg-slate-50"
              />
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-slate-50"
              />
            </div>

            <div className="space-y-2">
              <Label>Calendar Color</Label>
              <div className="flex gap-2 flex-wrap items-center">
                {CALENDAR_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full ${editForm.calendar_color === color ? 'ring-2 ring-offset-2 ring-slate-900' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setEditForm(prev => ({ ...prev, calendar_color: color }))}
                    aria-label={`Pick color ${color}`}
                  />
                ))}
                {/* Custom color picker — lets users dial in any HEX/HSL beyond
                    the preset swatches. The native input renders a checker-
                    pattern thumbnail; we overlay a small "+" badge so it
                    visually reads as "add custom color". */}
                <label
                  className={`relative w-8 h-8 rounded-full cursor-pointer overflow-hidden border-2 border-dashed border-slate-300 hover:border-amber-400 transition-colors flex items-center justify-center ${
                    editForm.calendar_color && !CALENDAR_COLORS.includes(editForm.calendar_color)
                      ? 'ring-2 ring-offset-2 ring-slate-900 border-solid'
                      : ''
                  }`}
                  title="Pick a custom color"
                  style={
                    editForm.calendar_color && !CALENDAR_COLORS.includes(editForm.calendar_color)
                      ? { backgroundColor: editForm.calendar_color, borderStyle: 'solid' }
                      : undefined
                  }
                  data-testid="custom-color-picker-swatch"
                >
                  <input
                    type="color"
                    value={editForm.calendar_color || '#000000'}
                    onChange={(e) => setEditForm(prev => ({ ...prev, calendar_color: e.target.value }))}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="custom-color-picker-input"
                  />
                  {!(editForm.calendar_color && !CALENDAR_COLORS.includes(editForm.calendar_color)) && (
                    <Plus className="w-4 h-4 text-slate-400" />
                  )}
                </label>
              </div>
              {/* Live preview + HEX entry so the user can paste a brand color */}
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-slate-500">HEX:</span>
                <Input
                  type="text"
                  value={editForm.calendar_color || ''}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    setEditForm(prev => ({ ...prev, calendar_color: v }));
                  }}
                  placeholder="#A0C4FF"
                  className="bg-slate-50 h-8 w-28 font-mono text-sm"
                  data-testid="calendar-color-hex-input"
                />
                {editForm.calendar_color && (
                  <span
                    className="inline-block w-6 h-6 rounded border border-slate-200"
                    style={{ backgroundColor: editForm.calendar_color }}
                    title="Preview"
                  />
                )}
              </div>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(value) => {
                  const isAdmin = value === 'admin';
                  setEditForm(prev => ({
                    ...prev,
                    role: value,
                    permissions: isAdmin ? {
                      create_jobs: true,
                      edit_jobs: true,
                      delete_jobs: true,
                      view_reports: true,
                      manage_team: true
                    } : prev.permissions
                  }));
                }}
              >
                <SelectTrigger className="bg-slate-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="technician">Technician</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Permissions */}
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium">Permissions</Label>
              <p className="text-xs text-slate-500">
                Admins and Super Users automatically have all permissions. For Managers and Technicians, toggle exactly what they should be able to access.
              </p>
              {(editForm.role === 'admin' || editForm.role === 'super_user') && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
                  <strong>Full access:</strong> All toggles below are locked ON because <code className="bg-emerald-100 px-1 rounded">{editForm.role}</code> accounts bypass every permission and page-visibility check automatically. These controls only apply to Manager and Technician roles.
                </div>
              )}

              {/* Group: Jobs */}
              <div className="space-y-2 pt-2">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Jobs</Label>
                {[
                  { k: 'create_jobs', label: 'Create Jobs' },
                  { k: 'edit_jobs', label: 'Edit Jobs' },
                  { k: 'delete_jobs', label: 'Delete Jobs' },
                  { k: 'assign_jobs', label: 'Assign Jobs to Team' },
                  { k: 'assign_equipment_to_jobs', label: 'Assign Equipment to Jobs (day-scoped reservations)' },
                ].map(p => (
                  <div key={p.k} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{p.label}</span>
                    <Switch
                      checked={editForm.role === 'admin' || editForm.role === 'super_user' ? true : !!editForm.permissions?.[p.k]}
                      onCheckedChange={(checked) => setEditForm(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, [p.k]: checked }
                      }))}
                      disabled={editForm.role === 'admin' || editForm.role === 'super_user'}
                      data-testid={`perm-${p.k}`}
                    />
                  </div>
                ))}
              </div>

              {/* Group: Proposals & Sales */}
              <div className="space-y-2 pt-2 border-t border-slate-200">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Proposals & Sales</Label>
                {[
                  { k: 'view_proposals', label: 'View Proposals' },
                  { k: 'create_proposals', label: 'Create / Edit Proposals & Opportunities' },
                ].map(p => (
                  <div key={p.k} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{p.label}</span>
                    <Switch
                      checked={editForm.role === 'admin' || editForm.role === 'super_user' ? true : !!editForm.permissions?.[p.k]}
                      onCheckedChange={(checked) => setEditForm(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, [p.k]: checked }
                      }))}
                      disabled={editForm.role === 'admin' || editForm.role === 'super_user'}
                      data-testid={`perm-${p.k}`}
                    />
                  </div>
                ))}
              </div>

              {/* Group: Certifications & Training */}
              <div className="space-y-2 pt-2 border-t border-slate-200">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Certifications & Training</Label>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">
                    Manage Certifications
                    <span className="block text-[11px] text-slate-400 font-normal">View / add / update training info for all employees</span>
                  </span>
                  <Switch
                    checked={
                      editForm.role === 'admin' || editForm.role === 'super_user' || editForm.role === 'manager'
                        ? true
                        : !!editForm.permissions?.manage_certifications
                    }
                    onCheckedChange={(checked) => setEditForm(prev => ({
                      ...prev,
                      permissions: { ...prev.permissions, manage_certifications: checked }
                    }))}
                    disabled={
                      editForm.role === 'admin' || editForm.role === 'super_user' || editForm.role === 'manager'
                    }
                    data-testid="perm-manage_certifications"
                  />
                </div>
              </div>

              {/* Group: Visibility */}
              <div className="space-y-2 pt-2 border-t border-slate-200">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Visibility</Label>
                {[
                  { k: 'view_all_jobs', label: 'View All Jobs (Dashboard + Time Tracker search — off = only their assigned jobs)' },
                  { k: 'view_reports', label: 'View Reports' },
                  { k: 'view_financials', label: 'View Financials ($ amounts, costs)' },
                  { k: 'office_access', label: 'Office Access (Timesheets / Expenses oversight)' },
                  { k: 'expense_any_job', label: 'Expense to Any Job (maintenance — charge any job, not just assigned)' },
                ].map(p => (
                  <div key={p.k} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{p.label}</span>
                    <Switch
                      checked={editForm.role === 'admin' || editForm.role === 'super_user' ? true : !!editForm.permissions?.[p.k]}
                      onCheckedChange={(checked) => setEditForm(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, [p.k]: checked }
                      }))}
                      disabled={editForm.role === 'admin' || editForm.role === 'super_user'}
                      data-testid={`perm-${p.k}`}
                    />
                  </div>
                ))}
              </div>

              {/* Group: Management */}
              <div className="space-y-2 pt-2 border-t border-slate-200">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Management</Label>
                {[
                  { k: 'manage_team', label: 'Manage Team Members (invite/edit)' },
                  { k: 'delete_team_members', label: 'Delete Team Members' },
                  { k: 'manage_contacts', label: 'Manage Contacts & Companies' },
                  { k: 'manage_services', label: 'Manage Services Catalog' },
                  { k: 'manage_equipment', label: 'Manage Equipment & Assets' },
                  { k: 'manage_calendar_events', label: 'Manage Calendar Events (PTO, meetings, training, etc.)' },
                ].map(p => (
                  <div key={p.k} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{p.label}</span>
                    <Switch
                      checked={editForm.role === 'admin' || editForm.role === 'super_user' ? true : !!editForm.permissions?.[p.k]}
                      onCheckedChange={(checked) => setEditForm(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, [p.k]: checked }
                      }))}
                      disabled={editForm.role === 'admin' || editForm.role === 'super_user'}
                      data-testid={`perm-${p.k}`}
                    />
                  </div>
                ))}
              </div>

              {/* Group: Approvals */}
              <div className="space-y-2 pt-2 border-t border-slate-200">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Approvals</Label>
                {[
                  { k: 'can_approve_field_reports', label: 'Approve Field Reports (PreCon / Locate / Soft Dig)' },
                  { k: 'can_receive_approval_notifications', label: 'Receive Field-Report Approval Notifications (for PDF prep & delivery)' },
                  { k: 'can_approve_maintenance_forms', label: 'Approve Maintenance / Equipment Forms' },
                  { k: 'can_receive_maintenance_notifications', label: 'Receive Maintenance / Equipment Notifications (approve / reject pings on forms they submit)' },
                ].map(p => (
                  <div key={p.k} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{p.label}</span>
                    <Switch
                      checked={editForm.role === 'admin' || editForm.role === 'super_user' ? true : !!editForm.permissions?.[p.k]}
                      onCheckedChange={(checked) => setEditForm(prev => ({
                        ...prev,
                        permissions: { ...prev.permissions, [p.k]: checked }
                      }))}
                      disabled={editForm.role === 'admin' || editForm.role === 'super_user'}
                      data-testid={`perm-${p.k}`}
                    />
                  </div>
                ))}
                {/* Unlike the toggles above, admin/super_user accounts are
                    NOT locked ON here — expense notifications are opt-in
                    only, so even admins must explicitly flip this on. */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Receive Expense Report Notifications</span>
                  <Switch
                    checked={!!editForm.permissions?.can_receive_expense_notifications}
                    onCheckedChange={(checked) => setEditForm(prev => ({
                      ...prev,
                      permissions: { ...prev.permissions, can_receive_expense_notifications: checked }
                    }))}
                    data-testid="perm-can_receive_expense_notifications"
                  />
                </div>
                <p className="text-xs text-slate-500 italic">Admins can opt in to this one; it is not automatic.</p>
              </div>
              {editForm.role === 'admin' && (
                <p className="text-xs text-slate-500 italic pt-2 border-t border-slate-200">Admins have all permissions enabled</p>
              )}
            </div>

            {/* Page Visibility — sidebar/url access (admins always see everything) */}
            <div className="space-y-2 pt-3 border-t border-slate-200" data-testid="page-visibility-group">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">Page Visibility</Label>
                <button
                  type="button"
                  onClick={() => setEditForm(prev => ({ ...prev, page_visibility: {} }))}
                  className="text-xs text-amber-600 hover:text-amber-700 underline"
                >
                  Show all
                </button>
              </div>
              <p className="text-xs text-slate-500 -mt-1">
                Toggles off hide the page from the sidebar and block direct URL access. Admins always see everything regardless.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {PAGE_VISIBILITY_ITEMS.map(p => {
                  const visible = editForm.page_visibility?.[p.key] !== false;
                  return (
                    <div key={p.key} className="flex items-center justify-between py-0.5">
                      <span className="text-sm text-slate-600">{p.label}</span>
                      <Switch
                        checked={editForm.role === 'admin' || editForm.role === 'super_user' ? true : visible}
                        onCheckedChange={(checked) => setEditForm(prev => ({
                          ...prev,
                          page_visibility: { ...(prev.page_visibility || {}), [p.key]: checked }
                        }))}
                        disabled={editForm.role === 'admin' || editForm.role === 'super_user'}
                        data-testid={`page-vis-${p.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={editForm.active}
                onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, active: checked }))}
              />
            </div>

            {/* Per-channel notification subscriptions — admin can disable
                specific firehoses for this member without touching their
                personal in-app/email prefs. Defaults to ON for every channel
                so unmigrated users continue to receive everything. */}
            <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="notification-subscriptions-section">
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                  Notifications
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Turn off channels this team member doesn&apos;t need. They also need their
                  own in-app / email toggle enabled (from the bell-icon settings) to receive alerts.
                </p>
              </div>
              {[
                { k: 'job_status', label: 'Job Status Changes', help: 'Pending → Scheduled → In Progress → Completed transitions' },
                { k: 'fr_pending', label: 'Field Report — Pending Review', help: 'When a technician submits a Locate / PreCon / Soft Dig for approval' },
                { k: 'fr_approved', label: 'Field Report — Approved', help: 'When a Locate / PreCon / Soft Dig you submitted gets approved' },
                { k: 'fr_ready_to_package', label: 'Field Report — Ready for Packaging', help: 'Fan-out to the packaging team once a report is approved' },
                { k: 'website_lead', label: 'Website Lead — New Inquiry', help: 'When a customer submits a form on cllocating.com (Contact / Locate / Soft Dig / CCTV)' },
              ].map(({ k, label, help }) => (
                <div key={k} className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">{label}</p>
                    <p className="text-xs text-slate-500">{help}</p>
                  </div>
                  <Switch
                    checked={editForm.notification_subscriptions?.[k] !== false}
                    onCheckedChange={(checked) =>
                      setEditForm((prev) => ({
                        ...prev,
                        notification_subscriptions: {
                          ...(prev.notification_subscriptions || {}),
                          [k]: checked,
                        },
                      }))
                    }
                    data-testid={`notif-sub-${k}`}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveMember}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                data-testid="save-member-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update
              </Button>
            </div>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">Invite Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={inviteForm.full_name}
                onChange={(e) => setInviteForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="John Doe"
                className="bg-slate-50"
                data-testid="invite-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@company.com"
                className="bg-slate-50"
                data-testid="invite-email-input"
              />
            </div>

            <p className="text-sm text-slate-500">
              An invite code will be generated. Share this code with the team member to join.
            </p>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendInvite}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                data-testid="send-invite-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Invite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={(open) => !open && handleCloseCreateUser()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed']">Create User Account</DialogTitle>
          </DialogHeader>
          
          {createdCredentials ? (
            // Success state - show credentials
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-semibold text-green-800">Account Created Successfully!</span>
                </div>
                <p className="text-sm text-green-700 mb-4">
                  Share these credentials with the new team member:
                </p>
                
                <div className="space-y-3 bg-white p-3 rounded border border-green-200">
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <p className="font-mono text-sm bg-slate-50 p-2 rounded">{createdCredentials.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Temporary Password</Label>
                    <p className="font-mono text-sm bg-amber-50 p-2 rounded border border-amber-200">{createdCredentials.temporary_password}</p>
                  </div>
                </div>
                
                <p className="text-xs text-slate-500 mt-3">
                  💡 Tip: The user should change their password after first login via Profile settings.
                </p>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={handleCloseCreateUser}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            // Form state
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={createUserForm.full_name}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="John Doe"
                    className="bg-slate-50"
                    data-testid="create-user-name-input"
                  />
                </div>
                
                <div className="space-y-2 col-span-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={createUserForm.email}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@company.com"
                    className="bg-slate-50"
                    data-testid="create-user-email-input"
                  />
                </div>
                
                <div className="space-y-2 col-span-2">
                  <Label>Password *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={createUserForm.password}
                      onChange={(e) => setCreateUserForm(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Minimum 6 characters"
                      className="bg-slate-50 font-mono"
                      data-testid="create-user-password-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={generatePassword}
                      className="shrink-0"
                    >
                      Generate
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Role *</Label>
                  <Select
                    value={createUserForm.role}
                    onValueChange={(value) => setCreateUserForm(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger className="bg-slate-50" data-testid="create-user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technician">Technician</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={createUserForm.title}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Field Technician"
                    className="bg-slate-50"
                  />
                </div>
                
                <div className="space-y-2 col-span-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={createUserForm.phone}
                    onChange={(e) => setCreateUserForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="bg-slate-50"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={handleCloseCreateUser}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateUser}
                  disabled={saving}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  data-testid="create-user-submit-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Account
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deactivate Dialog — the endpoint is soft-deactivate by default:
          is_active=false, all history/timesheets/job assignments preserved,
          reversible via the Reactivate button. */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="deactivate-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedMember?.full_name} will be hidden from the standard team list and can
              no longer receive new job assignments or log in.
              <br /><br />
              <span className="font-semibold text-slate-700">Their history is preserved.</span>{' '}
              All timesheets, job assignments, field reports, and photos remain intact and
              searchable. Finalize payroll by toggling <em>&ldquo;Show inactive employees&rdquo;</em>{' '}
              on the Team Timesheets tab.
              <br /><br />
              This can be reversed at any time using the <em>Reactivate</em> button.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMember}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
              data-testid="deactivate-confirm"
            >
              {saving ? 'Deactivating…' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordResetDialog} onOpenChange={(open) => {
        setShowPasswordResetDialog(open);
        if (!open) setResetResult(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          
          {resetResult ? (
            // Show result after password reset
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium mb-2">Password Reset Successful!</p>
                <p className="text-sm text-green-700">
                  New temporary password for <strong>{resetResult.user_name || selectedMember?.email}</strong>:
                </p>
              </div>
              
              <div className="p-4 bg-amber-50 border-2 border-dashed border-amber-300 rounded-lg text-center">
                <p className="text-xs text-amber-600 mb-1">Temporary Password</p>
                <p className="font-mono text-xl font-bold text-slate-900 tracking-wider">
                  {resetResult.password}
                </p>
              </div>
              
              {resetResult.email_sent ? (
                <p className="text-sm text-green-600 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email notification sent to {selectedMember?.email}
                </p>
              ) : (
                <p className="text-sm text-amber-600 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email not sent - please share the password manually
                </p>
              )}
              
              <p className="text-xs text-slate-500">
                The user should change their password after signing in.
              </p>
              
              <Button
                onClick={() => {
                  setShowPasswordResetDialog(false);
                  setResetResult(null);
                }}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900"
              >
                Done
              </Button>
            </div>
          ) : (
            // Show options
            <div className="space-y-4 pt-4">
              <p className="text-sm text-slate-600">
                Reset password for <strong>{selectedMember?.full_name}</strong> ({selectedMember?.email})
              </p>
              
              <div className="space-y-3">
                {/* Option 1: Send Reset Link */}
                <div className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Send className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900">Send Reset Link</h4>
                      <p className="text-xs text-slate-500 mb-3">
                        User receives an email with a secure link to create their own password
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        onClick={handleSendResetLink}
                        disabled={saving}
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                        Send Email
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Option 2: Set Temporary Password */}
                <div className="p-4 border border-slate-200 rounded-lg hover:border-amber-300 hover:bg-amber-50/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Key className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900">Set Temporary Password</h4>
                      <p className="text-xs text-slate-500 mb-3">
                        Generate a temporary password immediately. User will be notified by email.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        onClick={handleSetTempPassword}
                        disabled={saving}
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                        Generate Password
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setShowPasswordResetDialog(false)}
              >
                Cancel
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PermissionsSnapshotDialog
        open={showSnapshotDialog}
        onOpenChange={setShowSnapshotDialog}
        members={members}
      />

      {/* Restore Deleted Team Member dialog — recreates a user who was
          hard-deleted in the past (e.g. Nate Upchurch) as INACTIVE so jobs
          and timesheets referencing their name can be re-linked. */}
      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="max-w-md" data-testid="restore-deleted-dialog">
          <DialogHeader>
            <DialogTitle>Restore Deleted Team Member</DialogTitle>
            <DialogDescription>
              Recreate someone who was deleted before we added soft-deactivate. They will
              be created as <strong>Inactive</strong> so they don't get new assignments.
              Reactivate them once their access should resume.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Full name *</Label>
              <Input
                value={restoreForm.full_name}
                onChange={(e) => setRestoreForm((p) => ({ ...p, full_name: e.target.value }))}
                data-testid="restore-name-input"
                placeholder="e.g. Nate Upchurch"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={restoreForm.email}
                onChange={(e) => setRestoreForm((p) => ({ ...p, email: e.target.value }))}
                data-testid="restore-email-input"
                placeholder="e.g. nupchurch@cllocating.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select
                  value={restoreForm.role}
                  onValueChange={(v) => setRestoreForm((p) => ({ ...p, role: v }))}
                >
                  <SelectTrigger data-testid="restore-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={restoreForm.title}
                  onChange={(e) => setRestoreForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Field Tech"
                  data-testid="restore-title-input"
                />
              </div>
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input
                value={restoreForm.phone}
                onChange={(e) => setRestoreForm((p) => ({ ...p, phone: e.target.value }))}
                placeholder="910-555-0000"
                data-testid="restore-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRestoreOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRestoreDeleted}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              data-testid="restore-deleted-save-btn"
            >
              Restore as Inactive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
